import trackClick from 'js/utils/trackClick';
import isInViewport from 'js/utils/isInViewport';
import { consoleWarn } from 'js/utils/console';

// TODO: This can be made more efficent by using a single click handler per card,
//   and checking the currentTarget for the specific location where the click originated.
function setUpClickTracking(sourceWorkId: number, designVariant: string) {
  // All LOSWP clicks must track the following
  const commonExtra = {
    source_work_id: sourceWorkId,
    design: designVariant,
  };

  // Sidebar related works tracking
  // Appear beside the embedded PDF on large breakpoints &
  // below the embedded PDF on small & medium breakpoints
  document
    .querySelectorAll('.js-related-work-sidebar-card')
    .forEach((currEl) => {
      // If the card has no height, it is not visible (?)
      // (not sure this is relevant for sidebar works)
      if ((currEl as HTMLElement).offsetHeight === 0) {
        return;
      }

      const titleLink = currEl.querySelector(
        '.js-related-work-grid-card-title',
      );
      // Only tracks the mobile download button since the desktop download button
      // opens the SUTD modal, which is tracked independently.
      const downloadLink = currEl.querySelector(
        '.js-related-work-grid-card-download-button',
      );
      const viewPdfLink = currEl.querySelector(
        '.js-related-work-grid-card-view-pdf',
      );
      //
      // New drawbridge designs have a little bookmark / save icon
      const saveLink = currEl.querySelector(
        '.js-related-work-grid-card-save-button',
      );

      // SUTD modal button, tracked differently from downloadLink
      const sutdButton = currEl.querySelector('.js-swp-download-button');

      if (titleLink === null) {
        consoleWarn(
          'LOSWP related work sidebar click tracking null title element error',
        );
        return;
      }

      const workPosition = currEl.getAttribute('data-collection-position');

      const additionalExtra = {
        work_position: workPosition,
        // Consider the card within the viewport if at least the title is fully visable
        above_fold: isInViewport(titleLink),
        sort_order: currEl.getAttribute('data-sort-order') as string,
      };

      const entityId = currEl.getAttribute('data-entity-id') as string;
      // SUTD modal uses attachment id instead of work id
      // (not sure if this inconsistency is desirable, but changing the grid card SUTD
      // to be work id would cause inconsistency with other SUTD modal click tracking)
      const attachmentId = sutdButton
        ? JSON.parse(sutdButton.getAttribute('data-signup-modal') as string)
            .attachmentId
        : null;

      const onClick = (
        source: string,
        extraData = {},
        attachmentId: number | null = null,
      ) => {
        trackClick({
          source,
          bucket: designVariant,
          entityId: attachmentId === null ? entityId : attachmentId,
          extra: {
            ...extraData,
            ...additionalExtra,
            ...commonExtra,
          },
        });
      };

      titleLink.addEventListener('click', () =>
        onClick('loswp--related-work-sidebar-card-title'),
      );
      downloadLink?.addEventListener('click', () =>
        onClick('loswp--related-work-sidebar-card-download-link'),
      );
      saveLink?.addEventListener('click', () =>
        onClick('loswp--related-work-sidebar-card-save-link'),
      );
      viewPdfLink?.addEventListener('click', () =>
        onClick('loswp--related-work-sidebar-card-view-pdf'),
      );
      sutdButton?.addEventListener('click', () =>
        onClick(
          'loswp--related-work-sidebar-card-download-pdf-modal',
          {
            target_work_id: entityId,
          },
          attachmentId,
        ),
      );

      const authorsLinks = currEl.querySelectorAll(
        '.js-related-work-grid-card-author',
      );
      authorsLinks.forEach((authorLink) => {
        const authorId = authorLink.getAttribute('data-author-id');
        authorLink.addEventListener('click', () =>
          onClick('loswp--related-work-sidebar-author-link', {
            author_id: authorId,
          }),
        );
      });
    });

  // Cited by works tracking
  document
    .querySelectorAll(
      '[class*="js-related-work-sidebar-card--cited-by"], [class*="js-safe-related-work-cited-by"]',
    )
    .forEach((currEl) => {
      // If the card has no height, it is not visible
      if ((currEl as HTMLElement).offsetHeight === 0) {
        return;
      }

      const titleLink = currEl.querySelector('.js-cited-by-grid-card-title');
      const downloadLink = currEl.querySelector(
        '.js-cited-by-grid-card-download-button',
      );
      const viewPdfLink = currEl.querySelector(
        '.js-cited-by-grid-card-view-pdf',
      );
      const saveLink = currEl.querySelector(
        '.js-cited-by-grid-card-save-button',
      );
      const sutdButton = currEl.querySelector('.js-swp-download-button');

      if (titleLink === null) {
        consoleWarn(
          'LOSWP cited by work click tracking null title element error',
        );
        return;
      }

      const workPosition = currEl.getAttribute('data-collection-position');

      const additionalExtra = {
        work_position: workPosition,
        above_fold: isInViewport(titleLink),
        sort_order: currEl.getAttribute('data-sort-order') as string,
      };

      const entityId = currEl.getAttribute('data-entity-id') as string;
      const attachmentId = sutdButton
        ? JSON.parse(sutdButton.getAttribute('data-signup-modal') as string)
            .attachmentId
        : null;

      const onClick = (
        source: string,
        extraData = {},
        attachmentId: number | null = null,
      ) => {
        trackClick({
          source,
          bucket: designVariant,
          entityId: attachmentId === null ? entityId : attachmentId,
          extra: {
            ...extraData,
            ...additionalExtra,
            ...commonExtra,
          },
        });
      };

      titleLink.addEventListener('click', () =>
        onClick('loswp--cited-by-work-card-title'),
      );
      downloadLink?.addEventListener('click', () =>
        onClick('loswp--cited-by-work-card-download-link'),
      );
      saveLink?.addEventListener('click', () =>
        onClick('loswp--cited-by-work-card-save-link'),
      );
      viewPdfLink?.addEventListener('click', () =>
        onClick('loswp--cited-by-work-card-view-pdf'),
      );
      sutdButton?.addEventListener('click', () =>
        onClick(
          'loswp--cited-by-work-card-download-pdf-modal',
          {
            target_work_id: entityId,
          },
          attachmentId,
        ),
      );

      const authorsLinks = currEl.querySelectorAll(
        '.js-cited-by-grid-card-author',
      );
      authorsLinks.forEach((authorLink) => {
        const authorId = authorLink.getAttribute('data-author-id');
        authorLink.addEventListener('click', () =>
          onClick('loswp--cited-by-work-author-link', {
            author_id: authorId,
          }),
        );
      });
    });

  // WSJ 'grid' tracking (it's purely vertical, so shaped more like a column than a grid)
  // Appears above embedded PDF
  document.querySelectorAll('.js-wsj-grid-card').forEach((currEl) => {
    // If the card has no height, it is not visible (?)
    // (not sure this is relevant for WSJ grid works)
    if ((currEl as HTMLElement).offsetHeight === 0) {
      return;
    }

    const titleLink = currEl.querySelector('.js-wsj-grid-card-title');
    // Only tracks the mobile download button since the desktop download button
    // opens the SUTD modal, which is tracked independently.
    const downloadLink = currEl.querySelector(
      '.js-wsj-grid-card-download-button',
    );
    const viewPdfLink = currEl.querySelector('.js-wsj-grid-card-view-pdf');
    // SUTD modal button, tracked differently from downloadLink
    const sutdButton = currEl.querySelector('.js-swp-download-button');

    if (titleLink === null) {
      consoleWarn(
        'LOSWP related work WSJ grid click tracking null title element error',
      );
      return;
    }

    const workPosition = currEl.getAttribute('data-collection-position');

    const additionalExtra = {
      work_position: workPosition,
      // Consider the card within the viewport if at least the title is fully visable
      above_fold: isInViewport(titleLink),
      sort_order: currEl.getAttribute('data-sort-order') as string,
    };

    const entityId = currEl.getAttribute('data-entity-id') as string;
    // SUTD modal uses attachment id instead of work id
    // (not sure if this inconsistency is desirable, but changing the grid card SUTD
    // to be work id would cause inconsistency with other SUTD modal click tracking)
    const attachmentId = sutdButton
      ? JSON.parse(sutdButton.getAttribute('data-signup-modal') as string)
          .attachmentId
      : null;

    const onClick = (
      source: string,
      extraData = {},
      attachmentId: number | null = null,
    ) => {
      trackClick({
        source,
        bucket: designVariant,
        entityId: attachmentId === null ? entityId : attachmentId,
        extra: {
          ...extraData,
          ...additionalExtra,
          ...commonExtra,
        },
      });
    };

    titleLink.addEventListener('click', () =>
      onClick('loswp--wsj-grid-card-title'),
    );
    downloadLink?.addEventListener('click', () =>
      onClick('loswp--wsj-grid-card-download-link'),
    );
    viewPdfLink?.addEventListener('click', () =>
      onClick('loswp--wsj-grid-card-view-pdf'),
    );
    sutdButton?.addEventListener('click', () =>
      onClick(
        'loswp--wsj-grid-card-download-pdf-modal',
        {
          target_work_id: entityId,
        },
        attachmentId,
      ),
    );

    const authorsLinks = currEl.querySelectorAll('.js-wsj-grid-card-author');
    authorsLinks.forEach((authorLink) => {
      const authorId = authorLink.getAttribute('data-author-id');
      authorLink.addEventListener('click', () =>
        onClick('loswp--wsj-grid-author-link', {
          author_id: authorId,
        }),
      );
    });
  });

  // Used for "grid" variant work card
  // work card mobile tracking (desktop tracking is handled by track_click on buttons)
  // again shouldn't need querySelectorAll, but in case work card exist in multiple places
  document.querySelectorAll('.js-swp-control-work-card').forEach((currEl) => {
    // all elements are mobile-only
    const downloadButton = currEl.querySelector('.js-download-pdf-button');
    const pdfPackButton = currEl.querySelector('.js-pdf-pack-button');
    const workCover = currEl.querySelector('.js-work-card-cover-container');
    // authors on work card are tracked via URL params, see
    // 'app/views/single_work/loswp/work_card/_authors.html.slim'

    const entityId = currEl.getAttribute('data-entity-id') as string;

    const onClick = (source: string, extraData = {}) => {
      trackClick({
        source,
        bucket: designVariant,
        entityId: entityId,
        extra: {
          ...extraData,
          ...commonExtra,
        },
      });
    };

    downloadButton?.addEventListener('click', () =>
      onClick('loswp--mobile-premium-priming-download-link'),
    );
    pdfPackButton?.addEventListener('click', () =>
      onClick('loswp--mobile-premium-priming-pdf-pack'),
    );
    workCover?.addEventListener('click', () =>
      onClick('loswp--mobile-swp-splash-paper-cover'),
    );
  });

  // Used for "ds_vanilla" variant work card click tracking
  document.querySelectorAll('.js-loswp-work-card').forEach((currEl) => {
    const entityId = currEl.getAttribute('data-entity-id') as string;

    const onClick = (source: string, extraData = {}) => {
      trackClick({
        source,
        bucket: designVariant,
        entityId: entityId,
        extra: {
          ...extraData,
          ...commonExtra,
        },
      });
    };

    const authorsLinks = currEl.querySelectorAll('.js-wsj-grid-card-author');

    authorsLinks.forEach((authorLink) => {
      const authorId = authorLink.getAttribute('data-author-id');
      authorLink.addEventListener('click', () =>
        onClick('loswp--author-link--work-card', {
          author_id: authorId,
        }),
      );
    });

    const doiLink = currEl.querySelector('.js-loswp-work-card-doi-link');

    doiLink?.addEventListener('click', () =>
      onClick('loswp--work-card--doi-link'),
    );
  });

  document
    .querySelectorAll('.js-related-research-interest')
    .forEach((currEl) => {
      const entityId = currEl.getAttribute('data-entity-id') as string;

      const onClick = (source: string) => {
        trackClick({
          source,
          bucket: designVariant,
          entityId: entityId,
          extra: commonExtra,
        });
      };

      currEl.addEventListener('click', () =>
        onClick('loswp--related-interest-link--sidebar'),
      );
    });

  document.querySelectorAll('.js-loswp-figures-prev').forEach((currEl) => {
    const onClick = (source: string) => {
      trackClick({
        source,
        bucket: designVariant,
        entityId: sourceWorkId,
      });
    };

    currEl.addEventListener('click', () =>
      onClick('loswp--figures-carousel-prev'),
    );
  });

  document.querySelectorAll('.js-loswp-figures-next').forEach((currEl) => {
    const onClick = (source: string) => {
      trackClick({
        source,
        bucket: designVariant,
        entityId: sourceWorkId,
      });
    };

    currEl.addEventListener('click', () =>
      onClick('loswp--figures-carousel-next'),
    );
  });

  document
    .querySelectorAll('.js-safe-related-works-related-work--view-more-button')
    .forEach((currEl) => {
      currEl.addEventListener('click', () => {
        trackClick({
          source: 'loswp--related-works--view-more-button',
          bucket: designVariant,
          entityId: sourceWorkId,
        });
      });
    });

  document
    .querySelectorAll('.js-safe-related-works-cited-by--view-more-button')
    .forEach((currEl) => {
      currEl.addEventListener('click', () => {
        trackClick({
          source: 'loswp--cited-by--view-more-button',
          bucket: designVariant,
          entityId: sourceWorkId,
        });
      });
    });

  document.querySelectorAll('.footer--content a').forEach((currEl) => {
    const onClick = (source: string, entityId: string) => {
      trackClick({
        source,
        bucket: designVariant,
        entityId: entityId,
        extra: commonExtra,
      });
    };

    const entityId = (currEl as HTMLAnchorElement).href as string;

    currEl.addEventListener('click', () =>
      onClick('loswp--footer-link', entityId),
    );
  });

  document.querySelectorAll('.header--container').forEach((currEl) => {
    const onClick = (source: string, entityId: string) => {
      trackClick({
        source,
        bucket: designVariant,
        entityId: entityId,
        extra: commonExtra,
      });
    };

    const links = currEl.querySelectorAll('a');
    links.forEach((link) => {
      const entityId = link.href as string;

      link.addEventListener('click', () =>
        onClick('loswp--header-link', entityId),
      );
    });
  });
}

export default setUpClickTracking;
